import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useRole } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { track, EVENTS } from "@/lib/analytics";
import PlaybooksPanel from "@/components/PlaybooksPanel";
import {
  Brain, Bot, Shield, Globe, Plus, Play, X, ChevronRight, Download,
  Terminal as TerminalIcon, FileText, AlertOctagon, Sparkles, History, Trash2, Lock, Zap,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const TRIGGERS = [
  { value: "Queda de link principal", label_en: "Primary link down" },
  { value: "Tentativa de invasão", label_en: "Intrusion attempt" },
  { value: "Latência acima de 100ms", label_en: "Latency above 100ms" },
  { value: "Uso de CPU > 90%", label_en: "CPU usage > 90%" },
  { value: "Pico de erros 5xx", label_en: "5xx error spike" },
];

const ICON_MAP = { shield: Shield, globe: Globe, bot: Bot };

function AgentCard({ a, onToggle, locale, runCount }) {
  const Icon = ICON_MAP[a.icon] || Bot;
  return (
    <div className={`border bg-[#0F172A] p-5 transition-all ${a.enabled ? "border-slate-800" : "border-slate-900 opacity-60"}`}
         data-testid={`agent-card-${a.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 flex items-center justify-center border border-[#1E3A8A]/40 bg-[#1E3A8A]/15 flex-shrink-0">
          <Icon className="w-5 h-5 text-[#60A5FA]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-display font-bold text-lg">{a.name}</h3>
            <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-[#1E3A8A]/40 text-[#60A5FA] uppercase">{a.domain}</span>
            {a.system && <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-emerald-500/40 text-emerald-300 uppercase">CORE</span>}
            {typeof runCount === "number" && runCount > 0 && (
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-amber-500/40 text-amber-300 uppercase" data-testid={`agent-runcount-${a.id}`}>
                {runCount} {locale === "pt" ? "execuções" : "runs"}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 leading-relaxed mb-3">{a.description}</p>
          <div className="space-y-1 text-[10px] font-mono">
            <div><span className="text-slate-500 uppercase tracking-wider">Trigger:</span> <span className="text-slate-200">{a.trigger}</span></div>
            <div><span className="text-slate-500 uppercase tracking-wider">{locale === "pt" ? "Instrução:" : "Instruction:"}</span> <span className="text-slate-300">{a.instruction}</span></div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Switch data-testid={`agent-toggle-${a.id}`} checked={a.enabled} onCheckedChange={(v) => onToggle(a, v)} />
          <span className="text-[10px] font-mono uppercase text-slate-500">{a.enabled ? "ON" : "OFF"}</span>
        </div>
      </div>
    </div>
  );
}

function ProgramAgentModal({ open, onClose, onCreated, locale }) {
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState(TRIGGERS[0].value);
  const [instruction, setInstruction] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !instruction.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/agents", { name, trigger, instruction, enabled });
      track("agent_created", { trigger });
      toast.success(locale === "pt" ? `Agente "${data.name}" criado` : `Agent "${data.name}" created`);
      onCreated(data);
      onClose();
      setName(""); setInstruction("");
    } catch {
      toast.error("Falha");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="program-agent-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-lg border border-[#1E3A8A] bg-[#0F172A] rounded-sm">
        <div className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500 mb-1">{locale === "pt" ? "Novo Agente" : "New Agent"}</div>
            <h3 className="font-display font-black text-xl tracking-tight">{locale === "pt" ? "Programar Agente de IA" : "Program AI Agent"}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 block mb-2">{locale === "pt" ? "Nome do Agente" : "Agent Name"}</label>
            <input data-testid="agent-name-input" value={name} onChange={(e) => setName(e.target.value)} required minLength={3}
              placeholder={locale === "pt" ? "Ex: Agente de Backup de Nuvem" : "Ex: Cloud Backup Agent"}
              className="w-full bg-[#020617] border border-slate-800 focus:border-[#3B82F6] px-3 py-2.5 text-sm outline-none" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 block mb-2">{locale === "pt" ? "Gatilho de Ativação" : "Activation Trigger"}</label>
            <select data-testid="agent-trigger-select" value={trigger} onChange={(e) => setTrigger(e.target.value)}
              className="w-full bg-[#020617] border border-slate-800 px-3 py-2.5 text-sm text-slate-100 outline-none">
              {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{locale === "pt" ? t.value : t.label_en}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 block mb-2">{locale === "pt" ? "Ação Autônoma (Prompt)" : "Autonomous Action (Prompt)"}</label>
            <textarea data-testid="agent-instruction-input" value={instruction} onChange={(e) => setInstruction(e.target.value)} required rows={4}
              placeholder={locale === "pt" ? "Se a latência passar de 100ms na unidade de Petrolina, migre o tráfego de VoIP para a Vivo e emita um alerta no Telegram." : "If latency exceeds 100ms in Petrolina, migrate VoIP traffic to Vivo and emit a Telegram alert."}
              className="w-full bg-[#020617] border border-slate-800 focus:border-[#3B82F6] px-3 py-2.5 text-sm outline-none resize-none" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="agent-enabled-switch" />
            <span className="text-xs text-slate-300">{locale === "pt" ? "Habilitado ao criar" : "Enabled on create"}</span>
          </div>
        </div>
        <div className="border-t border-slate-800 px-6 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs text-slate-400 hover:text-white px-4 py-2 border border-slate-800">{locale === "pt" ? "Cancelar" : "Cancel"}</button>
          <button type="submit" data-testid="agent-submit-btn" disabled={submitting}
            className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white px-4 py-2 text-xs font-semibold flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> {submitting ? "..." : (locale === "pt" ? "Criar Agente" : "Create Agent")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Terminal({ logs, running }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" }); }, [logs]);
  return (
    <div ref={ref} className="bg-black border border-emerald-500/20 p-4 font-mono text-[12px] max-h-[420px] overflow-y-auto" data-testid="sentinel-terminal" style={{ fontFamily: "JetBrains Mono, IBM Plex Mono, monospace" }}>
      {logs.length === 0 && !running && (
        <div className="text-slate-600">$ awaiting Sentinel investigation trigger...<span className="evos-blink">_</span></div>
      )}
      {logs.map((l, i) => (
        <div key={i} className="text-emerald-300 leading-relaxed">
          <span className="text-slate-500">[{new Date().toISOString().slice(11, 19)}]</span>{" "}
          <span className="text-[#60A5FA]">[Agente Sentinel]</span> {l.icon} {l.text}
        </div>
      ))}
      {running && <div className="text-emerald-500 evos-blink">▍</div>}
    </div>
  );
}

function IncidentReport({ report, locale, onRunRecommended, canWrite, running }) {
  const rec = report.recommended_playbook;
  return (
    <div className="border border-red-500/40 bg-gradient-to-br from-[#1c0a0a] to-[#0F172A] p-6 space-y-4" data-testid="incident-report">
      <div className="flex items-start justify-between border-b border-slate-800 pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-red-300 mb-1">{locale === "pt" ? "Relatório de Incidente de Segurança" : "Security Incident Report"}</div>
          <div className="font-display font-black text-xl">{report.incident_id}</div>
        </div>
        <span className="text-[10px] font-mono font-bold px-2 py-1 border border-red-500/40 bg-red-500/15 text-red-300 evos-pulse">● {report.severity}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <Field label={locale === "pt" ? "Ameaça" : "Threat"} value={report.threat} />
        <Field label="IP / Geo" value={`${report.source_ip} (${report.geo})`} mono />
        <Field label="ASN" value={report.asn} mono />
        <Field label={locale === "pt" ? "Resposta" : "Response"} value={report.action} />
        <Field label={locale === "pt" ? "Tempo de resposta" : "Response time"} value={`${report.response_time_s}s · ${report.agent}`} />
        <Field label={locale === "pt" ? "Gerado em" : "Generated"} value={new Date(report.generated_at).toLocaleString(locale === "pt" ? "pt-BR" : "en-GB", { hour12: false })} mono />
      </div>

      {/* Recommended playbook — closes the loop investigation → recommendation → execution */}
      {rec && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="recommended-playbook">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 flex items-center justify-center border border-emerald-500/40 bg-emerald-500/10 flex-shrink-0">
              <Sparkles className="w-4 h-4 text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300 mb-1">
                {locale === "pt" ? "Playbook Recomendado pelo Agente" : "Playbook Recommended by Agent"}
              </div>
              <div className="font-display font-bold text-base text-slate-100">{rec.name}</div>
              <div className="text-[11px] text-slate-400 mt-1">{rec.reason}</div>
            </div>
            <button
              data-testid="run-recommended-btn"
              onClick={() => onRunRecommended?.(rec)}
              disabled={!canWrite || running}
              title={!canWrite ? (locale === "pt" ? "Acesso somente leitura" : "Read-only access") : ""}
              className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 self-center whitespace-nowrap"
            >
              {canWrite ? <Play className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
              {running ? (locale === "pt" ? "Executando..." : "Running...") : (locale === "pt" ? "Executar agora" : "Run now")}
            </button>
          </div>
        </div>
      )}

      <button data-testid="download-report-btn"
        onClick={() => toast.success(locale === "pt" ? "Download iniciado" : "Download started", { description: `${report.incident_id}.pdf` })}
        className="bg-red-500/15 hover:bg-red-500/25 border border-red-500/40 text-red-200 px-4 py-2 text-xs font-semibold flex items-center gap-2">
        <Download className="w-3.5 h-3.5" /> {locale === "pt" ? "Baixar Relatório do Agente" : "Download Agent Report"}
      </button>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="border border-slate-800 bg-[#020617] p-3">
      <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} text-slate-100`}>{value}</div>
    </div>
  );
}

export default function Agents() {
  const { t, locale } = useI18n();
  const { canWrite } = useRole();
  const { tenant } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [executingRec, setExecutingRec] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedInvestigation, setSelectedInvestigation] = useState(null);
  const runInvestigationRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/agents/investigations?limit=20");
      setHistory(data || []);
    } catch (_) { /* noop */ }
  };

  useEffect(() => { api.get("/agents").then(({ data }) => setAgents(data)); loadHistory(); }, []);

  // Auto-trigger an investigation when redirected with ?investigate=<ip> (e.g. from SOC simulator).
  // Also honor ?tab=playbooks|agents from sidebar shortcuts / legacy /playbooks redirect.
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(location.search).get("tab");
    return p === "playbooks" ? "playbooks" : "agents";
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ip = params.get("investigate");
    const requestedTab = params.get("tab");
    if (requestedTab && (requestedTab === "playbooks" || requestedTab === "agents")) {
      setTab(requestedTab);
    }
    if (!ip) return;
    // Defer ALL state writes so the effect itself doesn't synchronously set state.
    const t = setTimeout(() => {
      navigate("/agents", { replace: true });
      setTab("agents");
      runInvestigationRef.current?.(ip);
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const switchTab = (next) => {
    setTab(next);
    navigate(next === "playbooks" ? "/agents?tab=playbooks" : "/agents", { replace: true });
  };

  const onToggle = async (a, enabled) => {
    setAgents((arr) => arr.map((x) => x.id === a.id ? { ...x, enabled } : x));
    try { await api.post(`/agents/${a.id}/toggle`, { enabled }); }
    catch { setAgents((arr) => arr.map((x) => x.id === a.id ? { ...x, enabled: !enabled } : x)); }
  };

  const runInvestigation = async (sourceIp) => {
    if (running) return;
    setLogs([]); setReport(null); setSelectedInvestigation(null); setRunning(true);
    try {
      const { data } = await api.post("/agents/investigate", sourceIp ? { source_ip: sourceIp } : {});
      // Stagger reveal each log entry
      for (let i = 0; i < data.logs.length; i++) {
        await new Promise((r) => setTimeout(r, data.logs[i].delay_ms || 400));
        setLogs((prev) => [...prev, data.logs[i]]);
      }
      await new Promise((r) => setTimeout(r, 400));
      setReport(data.report);
      loadHistory();
    } finally {
      setRunning(false);
    }
  };
  runInvestigationRef.current = runInvestigation;

  const emptyLogsRef = useRef([]);

  const clearTerminal = () => {
    setLogs(emptyLogsRef.current);
    setReport(null);
    setSelectedInvestigation(null);
  };

  const viewInvestigation = (inv) => {
    setRunning(false);
    setSelectedInvestigation(inv.incident_id);
    setLogs(inv.logs || []);
    setReport(inv.report);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const runRecommended = async (rec) => {
    if (!rec || !tenant || executingRec) return;
    setExecutingRec(true);
    try {
      const { data } = await api.post(`/playbooks/${rec.id}/run?tenant=${tenant.id}`);
      toast.success(rec.name + " " + (locale === "pt" ? "executado" : "executed"), {
        description: (locale === "pt" ? `${data.log?.length || 0} passos OK · veja na aba Playbooks` : `${data.log?.length || 0} steps OK · view in Playbooks tab`),
        action: { label: locale === "pt" ? "Ver" : "View", onClick: () => switchTab("playbooks") },
      });
      // Auto-switch to the playbooks tab so the user sees the history row update
      setTimeout(() => switchTab("playbooks"), 600);
    } catch {
      toast.error(locale === "pt" ? "Falha ao executar playbook" : "Playbook execution failed");
    } finally {
      setExecutingRec(false);
    }
  };

  // Build run counts per agent_id
  const runCounts = history.reduce((acc, h) => {
    const k = h.agent_id || "agent-sentinel";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="border-b border-slate-800 pb-5 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono mb-2">
            {locale === "pt" ? "Administração Agêntica · Orquestração Autônoma" : "Agentic Administration · Autonomous Orchestration"}
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">
            {locale === "pt" ? "Administração Agêntica" : "Agentic Administration"}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {locale === "pt" ? "Agentes de IA e playbooks automatizados respondem a incidentes de rede e segurança." : "AI agents and automated playbooks respond to network and security incidents."}
          </p>
        </div>
        {tab === "agents" && (
          <button data-testid="program-agent-btn" onClick={() => setShowModal(true)} disabled={!canWrite}
            title={!canWrite ? (locale === "pt" ? "Acesso somente leitura" : "Read-only access") : ""}
            className="bg-[#1E3A8A] hover:bg-[#1D4ED8] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2.5 text-sm font-semibold flex items-center gap-2">
            {canWrite ? <Plus className="w-4 h-4" /> : <Lock className="w-4 h-4" />} {locale === "pt" ? "Programar Novo Agente" : "Program New Agent"}
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex border-b border-slate-800 -mt-2" data-testid="agents-tabs">
        <button
          data-testid="tab-agents-btn"
          onClick={() => switchTab("agents")}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 transition-colors ${
            tab === "agents" ? "border-[#60A5FA] text-[#60A5FA]" : "border-transparent text-slate-500 hover:text-slate-300"
          }`}
        >
          <Bot className="w-4 h-4" />
          {locale === "pt" ? "Agentes Autônomos" : "Autonomous Agents"}
        </button>
        <button
          data-testid="tab-playbooks-btn"
          onClick={() => switchTab("playbooks")}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 transition-colors ${
            tab === "playbooks" ? "border-[#60A5FA] text-[#60A5FA]" : "border-transparent text-slate-500 hover:text-slate-300"
          }`}
        >
          <Zap className="w-4 h-4" />
          {locale === "pt" ? "Playbooks de Automação" : "Automation Playbooks"}
        </button>
      </div>

      {tab === "playbooks" ? (
        <PlaybooksPanel />
      ) : (
        <>
          {/* Agents panel */}
          <div>
            <h2 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold mb-3 text-slate-300">
              {locale === "pt" ? "Painel de Controle dos Agentes" : "Agent Control Panel"}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {agents.map((a) => <AgentCard key={a.id} a={a} onToggle={onToggle} locale={locale} runCount={runCounts[a.id]} />)}
            </div>
          </div>

          {/* Live terminal */}
          <div className="border border-emerald-500/30 bg-[#0F172A]">
            <div className="border-b border-emerald-500/30 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <TerminalIcon className="w-4 h-4 text-emerald-400" />
                <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
                  {selectedInvestigation
                    ? (locale === "pt" ? `Visualizando · ${selectedInvestigation}` : `Viewing · ${selectedInvestigation}`)
                    : (locale === "pt" ? "Terminal de Investigação Autônoma · SOC Agent Live" : "Autonomous Investigation Terminal · SOC Agent Live")}
                </h3>
                {running && <span className="text-[10px] font-mono text-emerald-400 evos-blink ml-2">● LIVE</span>}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="clear-terminal-btn" onClick={clearTerminal} disabled={running || (logs.length === 0 && !report)}
                  className="border border-slate-700 hover:border-slate-500 disabled:opacity-30 text-slate-300 px-3 py-1.5 text-xs font-mono uppercase tracking-wider flex items-center gap-1.5">
                  <Trash2 className="w-3 h-3" /> {locale === "pt" ? "Limpar" : "Clear"}
                </button>
                <button data-testid="run-investigation-btn" onClick={() => runInvestigation()} disabled={running || !canWrite}
                  title={!canWrite ? (locale === "pt" ? "Acesso somente leitura" : "Read-only access") : ""}
                  className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                  {canWrite ? <Play className="w-3 h-3" /> : <Lock className="w-3 h-3" />} {running ? (locale === "pt" ? "Investigando..." : "Investigating...") : (locale === "pt" ? "Simular Incidente" : "Simulate Incident")}
                </button>
              </div>
            </div>
            <div className="p-4">
              <Terminal logs={logs} running={running} />
            </div>
          </div>

          {report && <IncidentReport report={report} locale={locale} onRunRecommended={runRecommended} canWrite={canWrite} running={executingRec} />}

          {/* Investigation history */}
          <div className="border border-slate-800 bg-[#0F172A]">
            <div className="border-b border-slate-800 px-5 py-3 flex items-center gap-2">
              <History className="w-4 h-4 text-[#60A5FA]" />
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-slate-300">
                {locale === "pt" ? "Histórico de Investigações" : "Investigation History"}
              </h3>
              <span className="text-[10px] font-mono text-slate-500">({history.length})</span>
            </div>
            <div className="divide-y divide-slate-800" data-testid="investigation-history">
              {history.length === 0 && (
                <div className="px-5 py-6 text-xs text-slate-500 font-mono">
                  {locale === "pt" ? "Nenhuma investigação registrada ainda. Clique em Simular Incidente." : "No investigations recorded yet. Click Simulate Incident."}
                </div>
              )}
              {history.map((inv) => (
                <button key={inv.incident_id} onClick={() => viewInvestigation(inv)}
                  data-testid={`history-row-${inv.incident_id}`}
                  className="w-full flex items-center gap-4 px-5 py-3 hover:bg-slate-800/40 text-left transition-colors">
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-red-500/40 bg-red-500/10 text-red-300 uppercase">
                    {inv.report?.severity || "CRÍTICA"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-slate-200">{inv.incident_id}</div>
                    <div className="text-[11px] text-slate-500 truncate">{inv.report?.threat}</div>
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 hidden md:block">
                    {inv.report?.source_ip}
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">
                    {new Date(inv.started_at).toLocaleString(locale === "pt" ? "pt-BR" : "en-GB", { hour12: false })}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <ProgramAgentModal open={showModal} onClose={() => setShowModal(false)} onCreated={(a) => setAgents((arr) => [...arr, a])} locale={locale} />
    </div>
  );
}
